import * as Sentry from '@sentry/react'
import { isLoggedInQuerySelector } from 'auth/selectors'
import {
  usePaymentsExtendSubscriptionMutation,
  usePaymentsStripeSubscriptionExtensionInfoQuery,
  paymentsStripeSubscriptionExtensionInfoQueryFetcher,
} from '../../gen/stripe'
import { useUserQuery } from 'auth/hooks'
import type { Language } from '../../gen/types'
import type { Platform } from 'cf-types'
import { IconButton } from 'primitives/buttons/icon-button'
import * as Dialog from 'primitives/dialog'
import { CalendarIcon, SpinnerIcon, XCloseIcon } from 'primitives/icons'
import * as React from 'react'
import { SentryFilterTagOneClickYearlyExtension } from '@repo/sentry-utils/constants'
import { tw } from 'utils/classnames'
import { trackExtensionPurchased } from '@repo/analytics/google-site-tag'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import { Z_INDEX_MODALS } from 'constants/modals'
import { DownloadButtonAnimation } from '@repo/animations/src/download-button-animation'
import translations from '../i18n/one-click-yearly-extension-button.json'
import { AnimatedSpinnerIcon } from './animated-spinner-icon'

export function OneClickYearlyExtensionButton(props: OneClickYearlyExtensionButtonProps) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => {
        scope.setTag('component', SentryFilterTagOneClickYearlyExtension)
      }}
    >
      <OneClickYearlyExtensionButtonComponent {...props} />
    </Sentry.ErrorBoundary>
  )
}

export interface OneClickYearlyExtensionButtonProps {
  label: string
  colors: {
    bgColor: string
    hoverBgColor: string
    textColor?: string
    confirmBgColor?: string
    confirmHoverBgColor?: string
  }
  lang: Language
  platform?: Platform
  className?: string
  enableTwoStepExtension?: boolean
  onClick?: () => void
}

type ExtensionStatus =
  | 'requiresAction'
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'idle'
  | 'userBanned'

// oxlint-disable-next-line complexity
export function OneClickYearlyExtensionButtonComponent({
  label,
  colors,
  lang,
  platform = 'react',
  className,
  enableTwoStepExtension = false,
  onClick,
}: OneClickYearlyExtensionButtonProps) {
  const { bgColor, hoverBgColor, confirmBgColor, confirmHoverBgColor, textColor } = colors
  const i18n = translations[lang]

  const { prices, currency } = usePricesForUser()

  const [canGetExtensionInfo, setCanGetExtensionInfo] = React.useState(true)
  const [isPolling, setIsPolling] = React.useState(false)
  const { data: userData, isPending: isUserLoading, refetch: refetchUser } = useUserQuery()

  const enableExtensionInfoFetching =
    !isUserLoading &&
    !!userData?.me?.user?.stripeCustomerId &&
    userData?.me?.user?.stripeCustomerId.length > 0 &&
    canGetExtensionInfo

  const { data: extensionInfoData, isPending: extensionInfoLoading } =
    usePaymentsStripeSubscriptionExtensionInfoQuery(
      {
        stripeCustomerId: userData?.me?.user?.stripeCustomerId || '',
        isPolling: isPolling,
        stripeAccount: userData?.me?.user?.stripeCustomer?.account,
      },
      {
        enabled: enableExtensionInfoFetching,
        refetchInterval: 30 * 1000,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
    )

  const isInProgress = extensionInfoData?.paymentsStripeSubscriptionExtensionInfo?.inProgress

  // eslint-disable-next-line cf-web/codegen-mutation-patterns
  const stripeExtendSubscription = usePaymentsExtendSubscriptionMutation()

  const [isLoading, setIsLoading] = React.useState(false)
  const [showExtensionModal, setShowExtensionModal] = React.useState(false)
  const [until, setUntil] = React.useState('')
  const [extensionStatus, setExtensionStatus] = React.useState<ExtensionStatus>('idle')
  const [showConfirmLabel, setShowConfirmLabel] = React.useState(false)

  React.useEffect(() => {
    if (!extensionInfoLoading && Boolean(isInProgress)) {
      setIsPolling(true)
    }
  }, [extensionInfoLoading, isInProgress])

  React.useEffect(() => {
    //If no data returned yet, do nothing
    if (isInProgress === undefined) {
      return
    }

    //If the extension is not in progress anymore, we can show the modal
    if (!isInProgress && !isLoading) {
      setCanGetExtensionInfo(false)
      const extensionData = extensionInfoData?.paymentsStripeSubscriptionExtensionInfo
      if (!extensionData || !extensionData.paymentStatus) {
        return
      }
      if (extensionData.paymentStatus === 'succeeded') {
        setUntil(
          new Date(extensionData.nextPaymentDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
        )
        trackExtensionPurchased()
      }
      if (extensionData.paymentStatus) {
        setExtensionStatus(extensionData.paymentStatus)
        setShowExtensionModal(true)
      }
    }
  }, [isInProgress, extensionInfoData?.paymentsStripeSubscriptionExtensionInfo, isLoading])

  const handleButtonClick = async () => {
    //only show confirm label if two step extension is enabled
    if (!showConfirmLabel && enableTwoStepExtension) {
      setShowConfirmLabel(true)
      return
    }

    setIsLoading(true)

    const { data: user } = await refetchUser()
    const isUserLoggedIn = isLoggedInQuerySelector(user)

    if (!isUserLoggedIn) {
      setIsLoading(false)
      return
    }

    if (user.me?.user?.stripeCustomerId && user.me?.user?.stripeCustomerId.length > 0) {
      onClick?.()

      //Get the latest extension info
      const extensionInfoData = await paymentsStripeSubscriptionExtensionInfoQueryFetcher({
        stripeCustomerId: user.me?.user?.stripeCustomerId,
        isPolling: false,
        stripeAccount: user.me?.user?.stripeCustomer?.account,
      })()

      //If the extension is in progress, we need to wait for it to finish before trying again
      if (extensionInfoData.paymentsStripeSubscriptionExtensionInfo?.inProgress) {
        setShowExtensionModal(true)
        return
      }

      const stripeSubscriptionId =
        extensionInfoData?.paymentsStripeSubscriptionExtensionInfo?.stripeSubscriptionId

      //If the user is a stripe subscriber, we need to extend the subscription via Stripe
      if (stripeSubscriptionId) {
        setCanGetExtensionInfo(true)
        stripeExtendSubscription.mutateAsync(
          {
            stripeSubscriptionId: stripeSubscriptionId,
            stripeAccount: user.me?.user?.stripeCustomer?.account,
          },
          {
            onError: error => {
              Sentry.captureException(
                new Error(
                  `Failed to extend subscription via payments service: ${
                    (error as Error)?.message ? (error as Error)?.message : (error as string)
                  }`,
                ),
                {
                  tags: {
                    component: SentryFilterTagOneClickYearlyExtension,
                  },
                },
              )
            },
            onSuccess: resp => {
              if (resp.paymentsExtendSubscription.userBanned) {
                setExtensionStatus('userBanned')
                setShowExtensionModal(true)
                return
              }
              if (resp.paymentsExtendSubscription.paymentStatus === 'succeeded') {
                setUntil(
                  new Date(resp.paymentsExtendSubscription.nextPaymentDate).toLocaleDateString(
                    'en-US',
                    { year: 'numeric', month: 'long', day: 'numeric' },
                  ),
                )
                trackExtensionPurchased()
              }
              setExtensionStatus(resp.paymentsExtendSubscription.paymentStatus)
              setShowExtensionModal(true)
            },
            // oxlint-disable-next-line require-await
            onSettled: async () => {
              setIsLoading(false)
              setShowConfirmLabel(false)
              setIsPolling(false)
            },
          },
        )
        return
      }
    }
  }

  const isExtensionLimitReached =
    stripeExtendSubscription?.data?.paymentsExtendSubscription.maxExtensionsReached === true

  const buttonStyle = showConfirmLabel
    ? 'bg-[--confirm-bg-color] hover:bg-[--confirm-hover-bg-color] text-[--text-color]'
    : 'bg-[--bg-color] hover:bg-[--hover-bg-color] text-[--text-color]'

  const hoverBtnColor = '#248142'
  const btnColor = '#3ab561'

  return (
    <>
      <button
        disabled={isLoading || isInProgress}
        onClick={handleButtonClick}
        style={{
          '--hover-bg-color': hoverBtnColor,
          '--bg-color': btnColor,
          '--confirm-hover-bg-color': confirmHoverBgColor ? confirmHoverBgColor : hoverBgColor,
          '--confirm-bg-color': confirmBgColor ? confirmBgColor : bgColor,
          '--text-color': textColor ? textColor : '#fff',
        }}
        className={tw(
          isLoading || isInProgress ? 'text-service-white bg-[#FA637B] opacity-80' : buttonStyle,
          'font-poppins flex min-h-[45px] w-full items-center justify-center rounded p-2 text-[1rem] font-bold uppercase',
          className,
        )}
      >
        {isLoading || isInProgress ? (
          <span className="flex items-center gap-1">
            <AnimatedSpinnerIcon />
            {i18n['banners.all_access_yearly.in_progress']}
          </span>
        ) : // oxlint-disable-next-line unicorn/no-nested-ternary
        showConfirmLabel ? (
          i18n['banners.all_access_yearly.confirm']
        ) : (
          <span className="flex items-center">
            <DownloadButtonAnimation className="mr-2 h-[28px] w-[28px]" />
            {label}
          </span>
        )}
      </button>

      <Dialog.Root
        open={showExtensionModal}
        onOpenChange={() => {
          setShowExtensionModal(false)
        }}
        defaultOpen={showExtensionModal}
      >
        <Dialog.Portal>
          <div data-cf-external>
            {/*z-index needs to be 10002 in this case.
            In Wordpress we show this on top of another one that has a z-index of 10000*/}
            <Dialog.Body
              dismissable={false}
              zIndex={
                platform === 'wordpress'
                  ? Z_INDEX_MODALS.onClickExtension.wp
                  : Z_INDEX_MODALS.onClickExtension.react
              }
            >
              <div className="font-poppins bg-service-white relative flex w-full max-w-[450px] flex-col items-center rounded-[12px] p-5 text-[#393D56]">
                {!isLoading && !isInProgress ? (
                  <>
                    {extensionStatus === 'userBanned' ? (
                      //userBanned
                      <h3 className="mt-5 text-center text-[16px] font-bold">
                        {i18n['banners.extend_all_access.modal.user_banned']}
                      </h3>
                    ) : null}

                    {extensionStatus !== 'failed' ? (
                      <>
                        <h2 className="text-lg font-bold uppercase">
                          {i18n['banners.extend_all_access.modal.title']}
                        </h2>
                        <h3 className="mt-5 text-center">
                          {i18n['banners.extend_all_access.modal.copy']}
                        </h3>
                      </>
                    ) : null}

                    {extensionStatus === 'requiresAction' ? (
                      //Requires action
                      <h3 className="mt-5 flex flex-col text-center text-[16px] font-bold">
                        {i18n['banners.extend_all_access.modal.requires_action']}
                        <a
                          className="underline"
                          target="_blank"
                          href={
                            stripeExtendSubscription?.data?.paymentsExtendSubscription
                              .payInvoiceUrl || ''
                          }
                          rel="noreferrer"
                        >
                          {i18n['banners.extend_all_access.modal.requires_action_cta']}
                        </a>
                      </h3>
                    ) : null}

                    {extensionStatus === 'processing' ? (
                      //payment processing
                      <h3 className="mt-5 text-center text-[16px] font-bold">
                        {i18n['banners.extend_all_access.modal.processing_copy']}
                      </h3>
                    ) : null}

                    {extensionStatus === 'succeeded' ? (
                      //payment succeeded
                      <>
                        <h3 className="mt-5 text-center text-[16px] font-bold">
                          {i18n['banners.extend_all_access.modal.extend_until']}
                        </h3>
                        <span className="mt-2 flex gap-1 text-center text-[16px] font-bold">
                          <img
                            src={CalendarIcon.src}
                            width="24"
                            height="24"
                            className="h-[24px] w-[24px]"
                          />
                          {until}
                        </span>
                      </>
                    ) : null}

                    {extensionStatus === 'failed' && !isExtensionLimitReached ? (
                      //payment failed
                      <h3 className="mt-5 flex flex-col text-center text-[16px] font-bold">
                        {!stripeExtendSubscription?.data?.paymentsExtendSubscription
                          .payInvoiceUrl ? (
                          <>
                            {i18n['banners.extend_all_access.modal.failed_no_url']}
                            <a
                              className="underline"
                              target="_blank"
                              href={'/my-account/payment-methods/'}
                              rel="noreferrer"
                            >
                              {i18n['banners.extend_all_access.modal.failed_no_url.cta']}
                            </a>
                          </>
                        ) : (
                          <>
                            {i18n['banners.extend_all_access.modal.failed']}
                            <a
                              className="underline"
                              target="_blank"
                              href={
                                stripeExtendSubscription?.data?.paymentsExtendSubscription
                                  .payInvoiceUrl || ''
                              }
                              rel="noreferrer"
                            >
                              {i18n['banners.extend_all_access.modal.requires_action_cta']}
                            </a>
                          </>
                        )}
                      </h3>
                    ) : null}

                    {extensionStatus === 'failed' && isExtensionLimitReached ? (
                      <h3 className="mt-5 text-center text-[16px] font-bold">
                        {i18n['banners.extend_all_access.modal.limit_reached']}
                      </h3>
                    ) : null}

                    {extensionStatus !== 'failed' ? (
                      <p className="mt-5 text-center text-base">
                        {i18n['banners.extend_all_access.modal.footnote']}
                      </p>
                    ) : null}
                    {extensionStatus === 'succeeded' ? (
                      <>
                        <button
                          onClick={handleButtonClick}
                          className="text-service-white mt-5 w-full rounded bg-[#493675] px-[10px] py-[5px] text-[10px] font-bold uppercase hover:bg-[#2a1f3f] md:px-5 md:py-[10px] md:text-sm"
                        >
                          {showConfirmLabel
                            ? i18n['banners.all_access_yearly.confirm']
                            : i18n['banners.extend_all_access.modal.extend_again_cta'].replace(
                                '{price}',
                                `${currency.symbol}${prices.yearlyPromoMonthlySplit}`,
                              )}
                        </button>
                        <button
                          onClick={() => setShowExtensionModal(false)}
                          className="mt-[10px] w-full rounded bg-[#e5def5] px-[10px] py-[5px] text-[10px] font-bold uppercase hover:bg-[#dbd0ff] md:px-5 md:py-[10px] md:text-sm"
                        >
                          {i18n['banners.extend_all_access.modal.cancel_button']}
                        </button>
                      </>
                    ) : null}
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-5">
                    <h2 className="text-lg font-bold uppercase">
                      {i18n['banners.extend_all_access.modal.in_progress_title']}
                    </h2>
                    <h3 className="text-center text-base font-bold">
                      {i18n['banners.all_access_yearly.in_progress']}
                    </h3>
                    <img
                      src={SpinnerIcon.src}
                      width="72"
                      height="72"
                      className="h-[72px] w-[72px] animate-spin"
                    />
                    <p
                      onClick={() => setShowExtensionModal(false)}
                      className="cursor-pointer text-base text-[#2572E4] underline"
                    >
                      {i18n['banners.extend_all_access.modal.in_progress.notify']}
                    </p>
                  </div>
                )}
                <div className="absolute right-[-18px] top-[-18px] [@media(min-width:1340px)]:-top-[52px] [@media(min-width:1340px)]:translate-y-full">
                  <Dialog.Close asChild>
                    <IconButton
                      variant="violet"
                      className="h-[36px] w-[36px] rounded-[50%] transition-transform hover:scale-110 active:scale-100"
                    >
                      <img
                        src={XCloseIcon.src}
                        width="30"
                        height="30"
                        className="h-[30px] w-[30px] [filter:invert(1)]"
                      />
                    </IconButton>
                  </Dialog.Close>
                </div>
              </div>
            </Dialog.Body>
          </div>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
